/**
 * Findet Passwörter, die mehrfach benutzt werden.
 *
 * Das kann nur im Browser passieren: der Server sieht ausschliesslich
 * Geheimtext und koennte zwei gleiche Passwoerter nie als gleich erkennen.
 * Daraus folgt eine Einschraenkung, die in der Anzeige auch so dasteht -
 * verglichen wird nur, was die angemeldete Person selbst entschluesseln kann.
 *
 * Die Vergleichswerte sind Hashes und bleiben im Arbeitsspeicher. Sie werden
 * nirgends gespeichert und nie verschickt: ein Hash ist zwar kein Passwort,
 * verraet aber sehr wohl, ob zwei Konten dasselbe benutzen.
 */
import type { ItemForUser } from '@shared/types';
import { toB64 } from './crypto';

export interface DuplicateUse {
  itemId: string;
  title: string;
  /** Wo genau: der Eintrag selbst oder einer seiner Plätze. */
  where: string;
}

export interface DuplicateGroup {
  /** Nur zum Unterscheiden der Gruppen in der Anzeige. Kein Geheimnis. */
  id: string;
  uses: DuplicateUse[];
}

export interface DuplicateReport {
  groups: DuplicateGroup[];
  /** Wie viele Passwörter überhaupt geprüft werden konnten. */
  checked: number;
  /** Wie viele Einträge verschlossen blieben, weil keine Freigabe vorliegt. */
  skipped: number;
}

export const EMPTY_REPORT: DuplicateReport = { groups: [], checked: 0, skipped: 0 };

async function fingerprint(password: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  // Ein Teil des Hashes genügt zum Gruppieren und macht Rückschlüsse noch unattraktiver.
  return toB64(new Uint8Array(digest).slice(0, 12));
}

/**
 * `read` liefert das entschluesselte Passwort oder null, wenn es keine Freigabe
 * gibt. Seriennummern bleiben aussen vor - dass dieselbe Lizenznummer auf
 * mehreren Plaetzen steht, ist der Normalfall und keine Warnung wert.
 */
export async function findDuplicates(
  items: ItemForUser[],
  read: (item: ItemForUser, assignmentId: string | null) => Promise<string | null>,
): Promise<DuplicateReport> {
  const byFingerprint = new Map<string, DuplicateUse[]>();
  let checked = 0;
  let skipped = 0;

  const record = async (password: string, use: DuplicateUse): Promise<void> => {
    const key = await fingerprint(password);
    const list = byFingerprint.get(key) ?? [];
    list.push(use);
    byFingerprint.set(key, list);
    checked += 1;
  };

  for (const item of items) {
    if (item.wrappedKey) {
      const password = await read(item, null);
      if (password) await record(password, { itemId: item.id, title: item.title, where: 'Eintrag' });
    } else {
      skipped += 1;
    }

    for (const assignment of item.assignments) {
      if (!assignment.wrappedKey || !assignment.secret) continue;
      // Bei gekauften Lizenzen steht in dem Feld die Seriennummer, nicht ein Passwort.
      if (item.kind === 'license') continue;
      const password = await read(item, assignment.id);
      if (password) {
        await record(password, {
          itemId: item.id,
          title: item.title,
          where: assignment.label || 'Platz',
        });
      }
    }
  }

  const groups: DuplicateGroup[] = [...byFingerprint.entries()]
    .filter(([, uses]) => uses.length > 1)
    .map(([id, uses]) => ({ id, uses }))
    .sort((a, b) => b.uses.length - a.uses.length);

  return { groups, checked, skipped };
}

/** Wie viele Stellen insgesamt ein mehrfach benutztes Passwort haben. */
export function affectedCount(report: DuplicateReport): number {
  return report.groups.reduce((sum, g) => sum + g.uses.length, 0);
}
